import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, RouterModule } from "@angular/router";
import { firstValueFrom } from "rxjs";
import { ApiService, SupplierDiyOrderDetail } from "../core/api.service";
import { controlLabel } from "../core/orders.util";
import { downloadMeasurementsPdf } from "./measurements-pdf.util";

@Component({
  selector: "app-diy-order-detail",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: "./diy-order-detail.component.html",
  styleUrls: ["./diy-order-detail.component.scss"],
})
export class DiyOrderDetailComponent {
  private readonly api = inject(ApiService);
  private readonly route = inject(ActivatedRoute);
  /** "job" = admin Own-Job (estimate), "diy" = storefront DIY order. */
  readonly kind: "job" | "diy" =
    (this.route.snapshot.data["kind"] as "job" | "diy") ?? "diy";
  private readonly id =
    this.route.snapshot.paramMap.get("orderId") ??
    this.route.snapshot.paramMap.get("customerId") ??
    "";

  readonly order = signal<SupplierDiyOrderDetail | null>(null);
  readonly summary = computed(() => {
    const items = this.order()?.items ?? [];
    const windows = items.filter((i) => !i.isRemote);
    const remotes = items.filter((i) => i.isRemote);
    return {
      rooms: new Set(windows.map((w) => w.roomName?.trim() || "Room")).size,
      windows: windows.length,
      remotes6: remotes.filter((r) => r.blindCode !== "REMOTE-16CH").length,
      remotes16: remotes.filter((r) => r.blindCode === "REMOTE-16CH").length,
      solar: windows.filter((w) => w.hasSolarPanel).length,
    };
  });

  /** Once the shop marks the supplier paid, the quote/Alibaba fields lock. */
  readonly paid = computed(() => this.order()?.quoteStatus === "paid");
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly busy = signal(false);
  readonly message = signal<string | null>(null);
  readonly messageError = signal(false);
  readonly quoteAmountInvalid = signal(false);
  readonly quoteFileInvalid = signal(false);
  readonly alibabaInvalid = signal(false);
  readonly trackingInvalid = signal(false);
  readonly measurementsDone = signal(false);
  readonly controlLabel = controlLabel;

  /** Supplier tasks for a DIY order, in order. */
  private readonly PROGRESS_STEPS = [
    "Requested",
    "Quote submitted",
    "Approved",
    "Alibaba link",
    "Paid",
    "Tracking number",
  ];

  /**
   * Progress through the supplier's tasks. The Alibaba link is what the shop
   * pays through, so it precedes "Paid" (and locks once paid); the tracking
   * number is the only task left after payment.
   */
  readonly progress = computed(() => {
    const o = this.order();
    const quote = o?.quoteStatus ?? "pending";
    const submitted =
      quote === "submitted" ||
      quote === "approved" ||
      quote === "paid" ||
      !!o?.quote;
    const done = [
      true,
      submitted,
      quote === "approved" || quote === "paid",
      !!o?.alibabaUrl,
      quote === "paid",
      !!o?.trackingNumber,
    ];
    const firstOpen = done.findIndex((d) => !d);
    return {
      steps: this.PROGRESS_STEPS,
      done,
      current: firstOpen < 0 ? done.length : firstOpen,
    };
  });

  private setMessage(text: string | null, error = false) {
    this.message.set(text);
    this.messageError.set(error);
  }

  setQuoteAmount(value: string) {
    this.quoteAmount = value;
    this.quoteAmountInvalid.set(false);
  }

  setAlibaba(value: string) {
    this.alibabaUrl = value;
    this.alibabaInvalid.set(false);
  }

  setTracking(value: string) {
    this.tracking = value;
    this.trackingInvalid.set(false);
  }

  quoteAmount = "";
  quoteFile: File | null = null;
  quoteFileName = "";
  tracking = "";
  alibabaUrl = "";

  constructor() {
    void this.load();
  }

  async load() {
    this.loading.set(true);
    this.error.set(null);
    try {
      const detail = await firstValueFrom(
        this.kind === "job"
          ? this.api.estimateDetail(this.id)
          : this.api.diyOrder(this.id),
      );
      this.order.set(detail);
      this.tracking = detail.trackingNumber ?? "";
      this.alibabaUrl = detail.alibabaUrl ?? "";
      this.quoteAmount = detail.quote?.amount != null ? String(detail.quote.amount) : "";
    } catch {
      this.error.set("Failed to load this order. Please try again.");
    } finally {
      this.loading.set(false);
    }
  }

  onFileSelected(input: Event) {
    const el = input.target as HTMLInputElement;
    const file = el.files?.[0] ?? null;
    this.quoteFile = file;
    this.quoteFileName = file?.name ?? "";
    this.quoteFileInvalid.set(false);
    this.setMessage(null);
  }

  async submitQuote() {
    const amount = Number(this.quoteAmount);
    const file = this.quoteFile;
    const amountOk = Number.isFinite(amount) && amount > 0;
    const fileOk = !!file;
    this.quoteAmountInvalid.set(!amountOk);
    this.quoteFileInvalid.set(!fileOk);
    if (!amountOk || !file) {
      this.setMessage(
        !file && amountOk
          ? "Attach the quote file before submitting."
          : "Enter the quote amount and attach the quote file.",
        true,
      );
      return;
    }
    this.busy.set(true);
    this.setMessage(null);
    try {
      const presign = await firstValueFrom(
        this.kind === "job"
          ? this.api.presignQuote(this.id)
          : this.api.presignDiyQuote(this.id),
      );
      const upload = await fetch(presign.uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type || "application/octet-stream",
        },
      });
      if (!upload.ok) {
        throw new Error(`Upload failed (${upload.status})`);
      }
      await firstValueFrom(
        this.kind === "job"
          ? this.api.submitQuote(this.id, {
              url: presign.publicUrl,
              name: file.name,
              amount,
            })
          : this.api.submitDiyQuote(this.id, {
              url: presign.publicUrl,
              name: file.name,
              amount,
            }),
      );
      this.setMessage("Quote submitted.");
      this.quoteFile = null;
      this.quoteFileName = "";
      await this.load();
    } catch {
      this.setMessage("Failed to submit the quote. Please try again.", true);
    } finally {
      this.busy.set(false);
    }
  }

  async setShipping() {
    const tracking = this.tracking.trim();
    this.trackingInvalid.set(!tracking);
    if (!tracking) {
      this.setMessage("Enter a tracking number.", true);
      return;
    }
    this.busy.set(true);
    this.setMessage(null);
    try {
      await firstValueFrom(
        this.kind === "job"
          ? this.api.setShipping(this.id, tracking)
          : this.api.setDiyShipping(this.id, tracking),
      );
      this.setMessage("Tracking saved and the customer was notified.");
      await this.load();
    } catch {
      this.setMessage("Failed to save tracking.", true);
    } finally {
      this.busy.set(false);
    }
  }

  async saveAlibaba() {
    const url = this.alibabaUrl.trim();
    const ok = /^https?:\/\/\S+/i.test(url);
    this.alibabaInvalid.set(!ok);
    if (!ok) {
      this.setMessage("Enter a valid Alibaba link (https://…).", true);
      return;
    }
    this.busy.set(true);
    this.setMessage(null);
    try {
      await firstValueFrom(
        this.kind === "job"
          ? this.api.setJobAlibaba(this.id, url)
          : this.api.setDiyAlibaba(this.id, url),
      );
      this.setMessage("Alibaba payment link saved.");
      await this.load();
    } catch {
      this.setMessage("Failed to save the link.", true);
    } finally {
      this.busy.set(false);
    }
  }

  statusLabel(status: string): string {
    return (status || "").replace(/_/g, " ");
  }

  roomGroups(o: SupplierDiyOrderDetail): {
    room: string;
    items: SupplierDiyOrderDetail["items"];
  }[] {
    const groups: { room: string; items: SupplierDiyOrderDetail["items"] }[] =
      [];
    const byRoom = new Map<string, (typeof groups)[number]>();
    for (const item of o.items) {
      const room = item.roomName?.trim() || "Room";
      let group = byRoom.get(room);
      if (!group) {
        group = { room, items: [] };
        byRoom.set(room, group);
        groups.push(group);
      }
      group.items.push(item);
    }
    return groups;
  }

  async downloadMeasurements() {
    const o = this.order();
    if (o) await downloadMeasurementsPdf(o);
    this.measurementsDone.set(true);
  }
}
